import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TIPS } from '../utils/onboardingTips';

const BORDER = '#D1D5DB';
const BG = '#FFFFFF';
const TEXT = '#374151';
const ACTION_BLUE = '#5A9ECB';
const MARGIN = 12;
const GAP = 12;

/** The first VISIBLE element marked data-tip-anchor="<anchor>" (desktop and mobile layouts both carry one). */
function findAnchor(anchor) {
  const nodes = document.querySelectorAll(`[data-tip-anchor="${anchor}"]`);
  for (const el of nodes) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

function isInFixedChrome(el) {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    const pos = window.getComputedStyle(n).position;
    if (pos === 'fixed' || pos === 'sticky') return true;
  }
  return false;
}

/**
 * White first-visit tip (Jason, 2026-10-20), same speech-bubble family as the
 * condition popover. Anchored by `data-tip-anchor`, follows scroll and resize,
 * hides while a modal dialog is open, scrolls its anchor into view when it
 * first appears, and closes only on "Got it".
 */
export default function OnboardingTip({ tipId, onGotIt, delay = 0 }) {
  const tip = TIPS[tipId];
  const boxRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [shown, setShown] = useState(false);
  const [dimmed, setDimmed] = useState(false);
  const scrolledRef = useRef(false);

  const measure = useCallback(() => {
    if (!tip) return;
    if (document.querySelector('[aria-modal="true"]')) { setLayout(null); return; }
    const el = findAnchor(tip.anchor);
    const box = boxRef.current;
    if (!el) { setLayout(null); return; }
    const a = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const bw = box ? box.offsetWidth : 280;
    const bh = box ? box.offsetHeight : 90;
    let placement = tip.placement;
    if (placement === 'auto') placement = a.top + a.height / 2 > vh / 2 ? 'above' : 'below';
    if (placement === 'left' && a.left - GAP - bw < MARGIN) placement = 'below';

    let left;
    let top;
    if (placement === 'left') {
      // Arrow near the bubble's top, pointing at the anchor's middle, so a
      // tip beside something at the very top of the page hangs downward
      // instead of rising off the page (web header, Jason 2026-10-20).
      left = a.left - GAP - bw;
      top = a.top + a.height / 2 - 26;
    } else {
      left = a.left + a.width / 2 - bw / 2;
      top = placement === 'above' ? a.top - GAP - bh : a.bottom + GAP;
    }
    // Only the sides are kept on screen. Vertically the tip stays with its
    // anchor, so scrolling away carries it away (it does not float).
    left = Math.max(MARGIN, Math.min(left, vw - bw - MARGIN));
    const arrowX = Math.max(16, Math.min(a.left + a.width / 2 - left, bw - 16));
    const arrowY = Math.max(16, Math.min(a.top + a.height / 2 - top, bh - 16));
    const fixed = isInFixedChrome(el);
    setLayout({
      // The anchor's own box and corner shape: only the object itself stays
      // undimmed, no padded block around it (Jason, 2026-10-20).
      spot: { left: a.left, top: a.top, width: a.width, height: a.height, radius: window.getComputedStyle(el).borderRadius || '0px' },
      fixed,
      left: fixed ? left : left + window.scrollX,
      top: fixed ? top : top + window.scrollY,
      placement, arrowX, arrowY,
    });
  }, [tip]);

  // First appearance: bring the anchor into view, then measure.
  useEffect(() => {
    if (!tip || scrolledRef.current) return undefined;
    let tries = 0;
    let t = null;
    const attempt = () => {
      // A modal dialog (welcome quickstart, first match, trade complete) owns the
      // screen: wait until it closes rather than dimming behind it. A tip whose
      // anchor never appears gives up quietly and never dims (review, 2026-10-20).
      if (document.querySelector('[aria-modal="true"]')) return false;
      const el = findAnchor(tip.anchor);
      tries += 1;
      if (!el) return tries > 40; // true = stop polling, nothing shown
      scrolledRef.current = true;
      setDimmed(true);
      let settle = 0;
      if (el && !isInFixedChrome(el)) {
        // Bring the thing the tip is about into view, with room for the bubble.
        const r = el.getBoundingClientRect();
        const offscreen = r.top < 120 || r.bottom > window.innerHeight - 200;
        if (offscreen && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          settle = 450; // let the smooth scroll land before the bubble appears
        }
      }
      measure();
      bubbleTimer = setTimeout(() => requestAnimationFrame(() => { measure(); setShown(true); }), settle + (delay ? 150 : 0));
      return true;
    };
    let bubbleTimer = null;
    const start = setTimeout(() => {
      if (!attempt()) {
        t = setInterval(() => { if (attempt()) clearInterval(t); }, 250);
      }
    }, delay);
    return () => { clearTimeout(start); clearTimeout(bubbleTimer); if (t) clearInterval(t); };
  }, [tip, measure, delay]);

  useLayoutEffect(() => {
    if (!tip) return undefined;
    let raf = 0;
    const onMove = () => { measure(); cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const poll = setInterval(measure, 500); // anchors that move without a scroll (layout shifts, modals)
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(poll);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [tip, measure]);

  if (!tip) return null;

  const p = layout ? layout.placement : 'below';
  const arrow = (() => {
    const base = { position: 'absolute', width: 0, height: 0 };
    if (p === 'left') {
      return [
        { ...base, right: -9, top: (layout?.arrowY || 20) - 9, borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderLeft: `9px solid ${BORDER}` },
        { ...base, right: -7, top: (layout?.arrowY || 20) - 7, borderTop: '7px solid transparent', borderBottom: '7px solid transparent', borderLeft: `7px solid ${BG}` },
      ];
    }
    const down = p === 'above';
    return [
      { ...base, left: (layout?.arrowX || 20) - 9, [down ? 'bottom' : 'top']: -9, borderLeft: '9px solid transparent', borderRight: '9px solid transparent', [down ? 'borderTop' : 'borderBottom']: `9px solid ${BORDER}` },
      { ...base, left: (layout?.arrowX || 20) - 7, [down ? 'bottom' : 'top']: -7, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', [down ? 'borderTop' : 'borderBottom']: `7px solid ${BG}` },
    ];
  })();

  return createPortal(
    <>
    {/* Dimmed page with a cut-out around the anchor (Jason, 2026-10-20, after
        TradeBlock). It also blocks taps on the page until "Got it". */}
    {/* The anchor scrolled off screen: the cut-out's shadow can no longer reach
        every corner, so dim the whole screen evenly instead. */}
    <style>{'@keyframes pxcTipDim { from { opacity: 0; } to { opacity: 1; } }'}</style>
    {dimmed && layout && layout.spot && (layout.spot.top + layout.spot.height < 0 || layout.spot.top > window.innerHeight) && (
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(17, 24, 39, 0.45)', pointerEvents: 'none', animation: 'pxcTipDim 350ms ease-out' }} />
    )}
    {dimmed && layout && layout.spot && !(layout.spot.top + layout.spot.height < 0 || layout.spot.top > window.innerHeight) && (
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: layout.spot.left,
          top: layout.spot.top,
          width: layout.spot.width,
          height: layout.spot.height,
          borderRadius: layout.spot.radius,
          boxShadow: '0 0 0 200vmax rgba(17, 24, 39, 0.45)',
          zIndex: 10000,
          pointerEvents: 'none',
          animation: 'pxcTipDim 350ms ease-out',
        }}
      />
    )}
    {/* Tap-blocker only while the bubble (and its Got it) is on screen. */}
    {dimmed && layout && layout.spot && (
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'transparent' }} />
    )}
    <div
      ref={boxRef}
      role="dialog"
      aria-label="Tip"
      style={{
        position: layout && !layout.fixed ? 'absolute' : 'fixed',
        left: layout ? layout.left : -9999,
        top: layout ? layout.top : -9999,
        zIndex: 10001,
        width: 'max-content',
        maxWidth: 'min(320px, calc(100vw - 24px))',
        background: BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 12,
        padding: '12px 14px 10px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        fontFamily: 'Nunito, sans-serif',
        color: TEXT,
        fontSize: 16,
        lineHeight: 1.45,
        opacity: shown && layout ? 1 : 0,
        transition: 'opacity 250ms ease-out',
        pointerEvents: layout ? 'auto' : 'none',
      }}
    >
      <div>{tip.text}</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <button
          type="button"
          onClick={() => onGotIt(tipId)}
          style={{ background: 'none', border: 'none', color: ACTION_BLUE, fontWeight: 800, fontSize: 16, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', padding: '4px 2px' }}
        >
          Got it
        </button>
      </div>
      <div aria-hidden="true" style={arrow[0]} />
      <div aria-hidden="true" style={arrow[1]} />
    </div>
    </>,
    document.body
  );
}
